import React, { useEffect } from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { useState } from "react";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Logo from "assets/Logo.svg";
import LogoWithoutBeta from "assets/LogoWithoutBeta.svg";
import HamburgerMenu from "assets/Icons/login/HamburgerMenu.svg";
import AdvertisingIcon from "assets/Icons/AdvertisingIcon.svg";
import { useQuery } from "react-query";
import { DRAWER_WIDTH } from "constants/constants";
import { useMediaQuery } from "@mui/material";
import { UserMenuNavbar } from "pages/shared/UserMenu/UserMenuNavbar";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "store/types";
import {
  addSelectedSellers,
  addSubscriptionDetails,
  addToken,
  addUserDetails,
  toggleNav,
} from "store/auth/auth.slice";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "router/types";
import { CircularProgress, Collapse, Grid, Skeleton } from "@mui/material";
import DashboardIcon from "assets/Icons/DashboardIcon.svg";
import DashboardIconBlack from "assets/Icons/dashboard/DashboardIconBlack.svg";
import InventoryIcon from "assets/Icons/InventoryIcon.svg";
import ReportsIcon from "assets/Icons/ReportsIcon.svg";
import CatalogIcon from "assets/Icons/CatalogIcon.svg";
import { useIntl } from "react-intl";
import I18nKey from "translations/I18nKey";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import Login from "pages/Login";
import { apiClient } from "api";
import { COMPARE } from "utils/utils";
import { SellersProps } from "components/types";
import Subscription from "components/Subscription";
import Popover from "components/Popover";
import { changeIsUserActive } from "store/Dashboard/dashboard.slice";
import {
  drawerToggleHandler,
  expandColor,
  iconBoxBackground,
  mainLayout,
  subscriptionBox,
} from "./style";
import { CURRENT_ENVIRONMENT, environments } from "constants/constants";
import { LoadingScreen } from "./LoadingScreen";
import classNames from "classnames";
import useLayoutStyles from "./style";
type Props = {
  children: JSX.Element | JSX.Element[];
};

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 0,
});
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: DRAWER_WIDTH,
    width: "100%",
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop: string) => prop !== "open",
})(({ theme, open }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
    "& .MuiDrawer-root .MuiDrawer-docked": {
      display: "none",
    },
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Layout = ({ children }: Props) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const theme = useTheme();
  const intl = useIntl();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, nav } = useSelector((state: AppState) => state.auth);
  const notificationToggle = useSelector(
    (state: AppState) => state.auth.notificationToggle
  );
  const { selectedSellers, subscription } = useSelector(
    (state: AppState) => state.auth
  );
  const user = useSelector((state: AppState) => state.auth.user);
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notification, setNotification] = React.useState<boolean>(false);
  const [collapseInventory, setCollapseInventory] =
    React.useState<boolean>(true);
  const [collapseReports, setCollapseReports] = React.useState<boolean>(true);
  const [collapseCatalog, setCollapseCatalog] = React.useState<boolean>(true);
  const [collapseAdvertising, setCollapseAdvertising] =
    React.useState<boolean>(true);
  const [active, setActive] = React.useState<string>("dashboard");
  const classes = useLayoutStyles();
  useEffect(() => {
    setOpen(nav);
  }, [nav]);

  const handleNotification = () => {
    setNotification(!notification);
  };

  useEffect(() => {
    setNotification(notificationToggle);
  }, [notificationToggle]);

  useEffect(() => {
    if (location.pathname === ROUTES.Main) {
      setActive("main");
    }
    if (location.pathname === ROUTES.Kepler) {
      setCollapseInventory(false);
      setActive("replenishment");
    }
    if (location.pathname === ROUTES.Landing) {
      setActive("dashboard");
    }
    if (location.pathname === ROUTES.Voyager) {
      setActive("salesView");
    }
    if (location.pathname === ROUTES.Apollo) {
      setActive("shipment");
    }
    if (location.pathname === ROUTES.Magellan) {
      setActive("forcasting");
    }
    if (location.pathname === ROUTES.Gemini) {
      setActive("listingHealth");
      setCollapseCatalog(false);
    }
    if (location.pathname === ROUTES.SalesChanges) {
      setActive("salesChanges");
    }
    if (location.pathname === ROUTES.Perseverance) {
      setActive("catalogTracker");
      setCollapseCatalog(false);
    }
    if (location.pathname === ROUTES.Rosetta) {
      setActive("performance");
      setCollapseReports(false);
    }
    if (location.pathname === ROUTES.Ulysses) {
      setActive("promotionCalendar");
      setCollapseAdvertising(false);
    }
    if (location.pathname === "sellers") {
      setActive("");
    }
    if (location.pathname === ROUTES.Pheonix) {
      setActive("payments");
      setCollapseReports(false);
    }
    if (
      location.pathname === ROUTES.CreateAccount ||
      location.pathname === ROUTES.ForgotPassword
    ) {
      setActive("");
      dispatch(toggleNav(false));
    }
    if (location.pathname === ROUTES.Products) {
      setActive("");
    }
  }, [location]);

  const getUserData = async () => {
    return await apiClient.getUserDetails(token);
  };

  const { data, isFetching, refetch } = useQuery("userData", getUserData, {
    staleTime: Infinity,
    retry: 1,
    retryDelay: 2000,
    enabled: true,
  });

  const getUserSubscriptionDetails = async () => {
    return await apiClient.getSubscriptionDetails(token);
  };

  const {
    data: SubsData,
    error: SubsError,
    refetch: refetchSubs,
  } = useQuery("userSubsData", getUserSubscriptionDetails, {
    staleTime: Infinity,
    retry: 1,
    retryDelay: 2000,
    enabled: false,
  });

  const getActiveUser = async () => {
    return await apiClient.userHasSellerOrNot();
  };

  const {
    data: activeUserData,
    refetch: activeUserRefetch,
    isFetching: activeUserIsFetching,
  } = useQuery("activeUser", getActiveUser, {
    staleTime: Infinity,
    retry: 1,
    retryDelay: 10000,
    enabled: false,
  });

  useEffect(() => {
    if (SubsData?.status === 200) {
      dispatch(addSubscriptionDetails(SubsData?.data?.data));
    } else if (
      SubsData?.status &&
      (SubsData?.status >= 400 || SubsData?.status <= 429)
    ) {
      dispatch(
        addSubscriptionDetails({ ...SubsData?.data?.errors, status: "error" })
      );
    }
  }, [SubsData]);

  useEffect(() => {
    if (activeUserData) {
      dispatch(
        changeIsUserActive(
          activeUserData?.data?.has_active_sellers === true &&
            subscription.status !== "error"
            ? 1
            : 0
        )
      );
      refetch();
      refetchSubs();
    }
  }, [activeUserData, subscription]);

  useEffect(() => {
    if (data) {
      dispatch(addUserDetails(data));

      //@ts-ignore
      if (data?.adminSellers?.length) {
        if (location.pathname.includes(ROUTES.Rosetta)) {
          //@ts-ignore
          let sellers = data?.adminSellers.map((item: any, index: number) => {
            return {
              id: item?.sellerId,
              name: item?.seller_name,
              checked: false,
            };
          });
          let newSellers: SellersProps[] = [];

          sellers[0].checked = true;
          newSellers = sellers;

          dispatch(addSelectedSellers(newSellers.sort(COMPARE)));
        } else if (location.pathname.includes(ROUTES.Pheonix)) {
          //@ts-ignore
          const sellers = data?.adminSellers.map((item: any) => {
            return {
              id: item?.sellerId,
              name: item?.seller_name,
              checked: false,
            };
          });
          let newSellers: SellersProps[] = [];

          sellers[0].checked = true;
          newSellers = sellers;

          dispatch(addSelectedSellers(newSellers.sort(COMPARE)));
        } else {
          //@ts-ignore
          const sellers = data?.adminSellers.map((item: any) => {
            return {
              id: item?.sellerId,
              name: item?.seller_name,
              checked: true,
            };
          });
          dispatch(addSelectedSellers(sellers.sort(COMPARE)));
        }
      }
      //@ts-ignore
      else if (data?.sellers?.length) {
        if (location.pathname.includes(ROUTES.Rosetta)) {
          const isPresent = selectedSellers.find(
            (item: SellersProps) => item.checked
          );

          //@ts-ignore
          let sellers = data?.sellers
            .map((item: any, index: number) => {
              return {
                id: item?.seller?.sellerId,
                name: item?.seller?.seller_name,
                checked: false,
              };
            })
            .sort(COMPARE);
          let newSellers: SellersProps[] = [];
          if (isPresent) {
            newSellers = sellers.map((item: SellersProps) => {
              if (item.id === isPresent.id) {
                return { ...item, checked: true };
              }
              return item;
            });
          } else {
            sellers[0].checked = true;
            newSellers = sellers;
          }

          dispatch(addSelectedSellers(newSellers.sort(COMPARE)));
        } else if (location.pathname.includes(ROUTES.Pheonix)) {
          const isPresent = selectedSellers.find(
            (item: SellersProps) => item.checked
          );

          //@ts-ignore
          const sellers = data?.sellers.map((item: any) => {
            return {
              id: item?.seller?.sellerId,
              name: item?.seller?.seller_name,
              checked: false,
            };
          });
          let newSellers: SellersProps[] = [];
          if (isPresent) {
            newSellers = sellers.map((item: SellersProps) => {
              if (item.id === isPresent.id) {
                return { ...item, checked: true };
              }
              return item;
            });
          } else {
            sellers[0].checked = true;
            newSellers = sellers;
          }

          dispatch(addSelectedSellers(newSellers.sort(COMPARE)));
        } else {
          //@ts-ignore
          const sellers = data?.sellers.map((item: any) => {
            return {
              id: item?.seller?.sellerId,
              name: item?.seller?.seller_name,
              checked: true,
            };
          });
          dispatch(addSelectedSellers(sellers.sort(COMPARE)));
        }
      } else {
        dispatch(addSelectedSellers([]));
      }
    } else if (data) {
      dispatch(addUserDetails(data));
      //@ts-ignore
      if (data?.adminSellers?.length) {
        //@ts-ignore
        let sellers = data?.adminSellers
          .map((item: any, index: number) => {
            return {
              id: item?.sellerId,
              name: item?.seller_name,
              checked: false,
            };
          })
          .sort(COMPARE);
        dispatch(addSelectedSellers(sellers));
      }
    }
  }, [data, location, token]);

  useEffect(() => {
    if (token) {
      localStorage.setItem("access_token", token);

      dispatch(addToken(token));
      activeUserRefetch();
      refetchSubs();
      if (data)
        setTimeout(() => {
          refetch();
        }, 1000);
      //@ts-ignore
    } else refetch();
  }, [token, data]);

  const handleDrawerToggle = () => {
    // setOpen(!open);
    // if (!location.pathname.includes(ROUTES.Pheonix))
    dispatch(toggleNav(!nav));

    // NOTE: Done for resizing Highcharts
    setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 300);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };
  const handleSetActive = (index: string) => {
    setActive(index);
  };

  const handleToggleInventory = () => setCollapseInventory(!collapseInventory);
  const handleToggleReports = () => setCollapseReports(!collapseReports);
  const handleToggleCatalog = () => setCollapseCatalog(!collapseCatalog);
  const handleToggleAdvertising = () =>
    setCollapseAdvertising(!collapseAdvertising);

  const getData = async (id: string) => {
    await apiClient
      .checkPaymentStatus(id)
      .then((res: any) => {})
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location?.search);
    const session_id = queryParams.get("session_id");
    if (session_id) {
      getData(session_id);
    }
  }, [location]);

  return (
    <Box sx={mainLayout}>
      <AppBar
        sx={{
          maxHeight: isMobile ? "fit - content" : "200px",
          background: "#002B49",
          padding: token ? "0" : "0 0 15px 0",
          overflowX: isMobile && token ? "auto" : null,
        }}
        position={isMobile && !token ? "absolute" : "fixed"}
        open={open}
      >
        <Toolbar
          sx={{
            display: "flex",
            flexDirection: token ? "row" : "column",
            alignItems: token ? "center" : "start",
            justifyContent: token ? "space-between" : "center",
          }}
        >
          <Box
            className={classNames(classes.drawerBox, {
              mobile: isMobile,
              token: token,
            })}
          >
            <Box
              sx={drawerToggleHandler}
              onClick={() => {
                if (
                  window.location.pathname !== ROUTES.CreateAccount &&
                  window.location.pathname !== ROUTES.ForgotPassword
                )
                  handleDrawerToggle();
              }}
            >
              {window.location.pathname === ROUTES.CreateAccount ||
              window.location.pathname === ROUTES.ForgotPassword ? null : (
                <img
                  src={HamburgerMenu}
                  alt="menu"
                  style={{ marginRight: "20px", zIndex: "10" }}
                />
              )}
              <Box>
                <img
                  src={
                    window.location.pathname === ROUTES.Sellers
                      ? LogoWithoutBeta
                      : Logo
                  }
                  alt="logo"
                  style={{ marginTop: "3px" }}
                />
              </Box>
            </Box>
          </Box>

          {window.location.pathname !== ROUTES.CreateAccount &&
          window.location.pathname !== ROUTES.ForgotPassword &&
          !token ? (
            <Login />
          ) : (
            <Box sx={subscriptionBox}>
              {(subscription?.status === "trialing" ||
                subscription?.status === "past_due" ||
                subscription?.status === "error") &&
              token ? (
                <Subscription />
              ) : (
                false
              )}
              {token &&
                (user?.user?.email === "demo@ridgewoodpartner.com" ? (
                  <CircularProgress color="inherit" />
                ) : (
                  <UserMenuNavbar
                    user={
                      Object.keys(user).length
                        ? user
                        : {
                            user: {
                              name: "Stephen Flem",
                              role: {
                                name: "Company Admin",
                              },
                            },
                          }
                    }
                  />
                ))}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        open={open}
        sx={{
          "& .MuiDrawer-root .MuiDrawer-docked": {
            display: !open ? "none" : "block",
          },
        }}
      >
        <Box sx={iconBoxBackground}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </Box>
        <Divider />
        <List
          sx={{
            background: "#002B49",
            height: "100%",
            paddingTop: !token ? "50px" : 6,
          }}
        >
          <ListItem
            key={1}
            disablePadding
            sx={{
              display: "block",
              background:
                active === "dashboard"
                  ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                  : "",
              backdropFilter: active === "dashboard" ? "blur(10px)" : "",
              borderBottom: "1px solid #113D5B",
            }}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
              }}
              onClick={() => {
                handleSetActive("dashboard");
                setActive("dashboard");
                navigate(ROUTES.Landing);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <img
                  src={
                    active === "dashboard" ? DashboardIcon : DashboardIconBlack
                  }
                  alt={"dashboard"}
                />
              </ListItemIcon>
              <ListItemText
                primary={intl.formatMessage({
                  id: I18nKey.DRAWER_LAYOUT_DASHBOARD,
                })}
                sx={{
                  opacity: open ? 1 : 0,
                  color: active === "dashboard" ? "white" : "#FFAB8C",
                  fontSize: 16,
                  "& .MuiTypography-root": {
                    fontFamily: "Lato",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
          <ListItem
            key={2}
            disablePadding
            sx={{
              display: "block",
              background:
                active === "inventory"
                  ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                  : "",
              backdropFilter: active === "inventory" ? "blur(10px)" : "",
              borderBottom: "1px solid #113D5B",
            }}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
              }}
              onClick={() => {
                handleToggleInventory();
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <img src={InventoryIcon} alt={"inventory"} />
              </ListItemIcon>
              <ListItemText
                primary={intl.formatMessage({
                  id: I18nKey.DRAWER_LAYOUT_INVENTORY,
                })}
                sx={{
                  opacity: open ? 1 : 0,
                  color: "#FFAB8C",
                  fontSize: 16,
                  "& .MuiTypography-root": {
                    fontFamily: "Lato",
                  },
                }}
              />
              {open &&
                (collapseInventory ? (
                  <ExpandMore sx={expandColor} />
                ) : (
                  <ExpandLess sx={expandColor} />
                ))}
            </ListItemButton>
            <Collapse
              in={!collapseInventory && open}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <ListItemButton
                  sx={{
                    pl: 4,
                    background:
                      active === "replenishment"
                        ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                        : "none",
                  }}
                  onClick={() => {
                    setActive("replenishment");
                    navigate(ROUTES.Kepler);
                  }}
                  key={1}
                >
                  <ListItemText
                    sx={{
                      color: active === "replenishment" ? "white" : "#FF6C37",
                      fontFamily: "Lato",
                      "& .MuiTypography-root": {
                        fontFamily: "Lato",
                      },
                      marginLeft: "30px",
                    }}
                    primary={intl.formatMessage({
                      id: I18nKey.DRAWER_LAYOUT_REPLENISHMENT,
                    })}
                  />
                </ListItemButton>

                <ListItemButton
                  key={2}
                  sx={{
                    pl: 4,
                    background:
                      active === "shipment"
                        ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                        : "none",
                  }}
                  onClick={() => {
                    setActive("shipment");
                    navigate(ROUTES.Apollo);
                  }}
                >
                  <ListItemText
                    sx={{
                      color: active === "shipment" ? "white" : "#FF6C37",
                      fontFamily: "Lato",
                      "& .MuiTypography-root": {
                        fontFamily: "Lato",
                      },
                      marginLeft: "30px",
                    }}
                    primary={intl.formatMessage({
                      id: I18nKey.DRAWER_LAYOUT_SHIPMENTS,
                    })}
                  />
                </ListItemButton>
                <ListItemButton
                  sx={{
                    pl: 4,
                    background:
                      active === "fulfillmentFees"
                        ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                        : "none",
                  }}
                  onClick={() => {
                    setActive("fulfillmentFees");
                    navigate(ROUTES.Ranger);
                  }}
                  key={2}
                >
                  <ListItemText
                    sx={{
                      color: active === "fulfillmentFees" ? "white" : "#FF6C37",
                      fontFamily: "Lato",
                      "& .MuiTypography-root": {
                        fontFamily: "Lato",
                      },
                      marginLeft: "30px",
                    }}
                    primary={intl.formatMessage({
                      id: I18nKey.DRAWER_LAYOUT_FULFILLMENTFEES,
                    })}
                  />
                </ListItemButton>
                <ListItemButton
                  sx={{
                    pl: 4,
                    background:
                      active === "forcasting"
                        ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                        : "none",
                  }}
                  onClick={() => {
                    setActive("forcasting");
                    navigate(ROUTES.Magellan);
                  }}
                  key={3}
                >
                  <ListItemText
                    sx={{
                      color: active === "forcasting" ? "white" : "#FF6C37",
                      fontFamily: "Lato",
                      "& .MuiTypography-root": {
                        fontFamily: "Lato",
                      },
                      marginLeft: "30px",
                    }}
                    primary={intl.formatMessage({
                      id: I18nKey.DRAWER_LAYOUT_FORECASTING,
                    })}
                  />
                </ListItemButton>
              </List>
            </Collapse>
          </ListItem>
          <ListItem
            key={3}
            disablePadding
            sx={{
              display: "block",
              background:
                active === "reports"
                  ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                  : "",
              backdropFilter: active === "reports" ? "blur(10px)" : "",
              borderBottom: "1px solid #113D5B",
            }}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
              }}
              onClick={() => {
                handleToggleReports();
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <img src={ReportsIcon} alt={"reports"} />
              </ListItemIcon>
              <ListItemText
                primary={intl.formatMessage({
                  id: I18nKey.DRAWER_LAYOUT_REPORTS,
                })}
                sx={{
                  opacity: open ? 1 : 0,
                  color: "#FFAB8C",
                  fontSize: 16,
                  "& .MuiTypography-root": {
                    fontFamily: "Lato",
                  },
                }}
              />
              {open &&
                (collapseReports ? (
                  <ExpandMore sx={expandColor} />
                ) : (
                  <ExpandLess sx={expandColor} />
                ))}
            </ListItemButton>
            <Collapse
              in={!collapseReports && open}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                {CURRENT_ENVIRONMENT !== environments.PRODUCTION && (
                  <ListItemButton
                    sx={{
                      pl: 4,
                      background:
                        active === "salesView"
                          ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                          : "none",
                    }}
                    onClick={() => {
                      setActive("salesView");
                      navigate(ROUTES.Voyager);
                    }}
                    key={1}
                  >
                    <ListItemText
                      sx={{
                        color: active === "salesView" ? "white" : "#FF6C37",
                        fontFamily: "Lato",
                        "& .MuiTypography-root": {
                          fontFamily: "Lato",
                        },
                        marginLeft: "30px",
                      }}
                      primary={intl.formatMessage({
                        id: I18nKey.DRAWER_LAYOUT_SALES_VIEW_REPORTS,
                      })}
                    />
                  </ListItemButton>
                )}
                <ListItemButton
                  sx={{
                    pl: 4,
                    background:
                      active === "salesChanges"
                        ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                        : "none",
                  }}
                  onClick={() => {
                    setActive("salesChanges");
                    navigate(ROUTES.SalesChanges);
                  }}
                  key={1}
                >
                  <ListItemText
                    sx={{
                      color: active === "salesChanges" ? "white" : "#FF6C37",
                      fontFamily: "Lato",
                      "& .MuiTypography-root": {
                        fontFamily: "Lato",
                      },
                      marginLeft: "30px",
                    }}
                    primary={intl.formatMessage({
                      id: I18nKey.DRAWER_LAYOUT_SALES_CHANGES,
                    })}
                  />
                </ListItemButton>

                <ListItemButton
                  sx={{
                    pl: 4,
                    background:
                      active === "performance"
                        ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                        : "none",
                  }}
                  onClick={() => {
                    setActive("performance");
                    navigate(ROUTES.Rosetta);
                  }}
                  key={4}
                >
                  <ListItemText
                    sx={{
                      color: active === "performance" ? "white" : "#FF6C37",
                      fontFamily: "Lato",
                      "& .MuiTypography-root": {
                        fontFamily: "Lato",
                      },
                      marginLeft: "30px",
                    }}
                    primary={intl.formatMessage({
                      id: I18nKey.DRAWER_LAYOUT_PERFORMANCE,
                    })}
                  />
                </ListItemButton>

                <ListItemButton
                  sx={{
                    pl: 4,
                    background:
                      active === "payments"
                        ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                        : "none",
                  }}
                  onClick={() => {
                    setActive("payments");
                    navigate(ROUTES.Pheonix);
                  }}
                  key={5}
                >
                  <ListItemText
                    sx={{
                      color: active === "payments" ? "white" : "#FF6C37",
                      fontFamily: "Lato",
                      "& .MuiTypography-root": {
                        fontFamily: "Lato",
                      },
                      marginLeft: "30px",
                    }}
                    primary={intl.formatMessage({
                      id: I18nKey.DRAWER_LAYOUT_PAYMENTS,
                    })}
                  />
                </ListItemButton>
              </List>
            </Collapse>
          </ListItem>
          <ListItem
            key={4}
            disablePadding
            sx={{
              display: "block",
              background:
                active === "catalog"
                  ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                  : "",
              backdropFilter: active === "reports" ? "blur(10px)" : "",
              borderBottom: "1px solid #113D5B",
            }}
          >
            <ListItemButton
              sx={{
                minHeight: 48,
                justifyContent: open ? "initial" : "center",
                px: 2.5,
              }}
              onClick={() => {
                handleToggleCatalog();
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : "auto",
                  justifyContent: "center",
                }}
              >
                <img src={CatalogIcon} alt={"reports"} />
              </ListItemIcon>
              <ListItemText
                primary={intl.formatMessage({
                  id: I18nKey.DRAWER_LAYOUT_CATALOG,
                })}
                sx={{
                  opacity: open ? 1 : 0,
                  color: "#FFAB8C",
                  fontSize: 16,
                  "& .MuiTypography-root": {
                    fontFamily: "Lato",
                  },
                }}
              />
              {collapseCatalog ? (
                <ExpandMore sx={expandColor} />
              ) : (
                <ExpandLess sx={expandColor} />
              )}
            </ListItemButton>
            <Collapse
              in={!collapseCatalog && open}
              timeout="auto"
              unmountOnExit
            >
              <List component="div" disablePadding>
                <ListItemButton
                  sx={{
                    pl: 4,
                    background:
                      active === "listingHealth"
                        ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                        : "none",
                  }}
                  onClick={() => {
                    setActive("listingHealth");
                    navigate(ROUTES.Gemini);
                  }}
                  key={1}
                >
                  <ListItemText
                    sx={{
                      color: active === "listingHealth" ? "white" : "#FF6C37",
                      fontFamily: "Lato",
                      "& .MuiTypography-root": {
                        fontFamily: "Lato",
                      },
                      marginLeft: "30px",
                    }}
                    primary={intl.formatMessage({
                      id: I18nKey.DRAWER_LAYOUT_LISTING_HEALTH,
                    })}
                  />
                </ListItemButton>
              </List>
              <List component="div" disablePadding>
                <ListItemButton
                  sx={{
                    pl: 4,
                    background:
                      active === "catalogTracker"
                        ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                        : "none",
                  }}
                  onClick={() => {
                    setActive("catalogTracker");
                    navigate(ROUTES.Perseverance);
                  }}
                  key={2}
                >
                  <ListItemText
                    sx={{
                      color: active === "catalogTracker" ? "white" : "#FF6C37",
                      fontFamily: "Lato",
                      "& .MuiTypography-root": {
                        fontFamily: "Lato",
                      },
                      marginLeft: "30px",
                    }}
                    primary={intl.formatMessage({
                      id: I18nKey.DRAWER_LAYOUT_CATALOG_TRACKER,
                    })}
                  />
                </ListItemButton>
              </List>
            </Collapse>
          </ListItem>
          {CURRENT_ENVIRONMENT !== environments.PRODUCTION && (
            <ListItem
              key={5}
              disablePadding
              sx={{
                display: "block",
                background:
                  active === "advertising"
                    ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                    : "",
                backdropFilter: active === "advertising" ? "blur(10px)" : "",
                borderBottom: "1px solid #113D5B",
              }}
            >
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                }}
                onClick={() => {
                  handleToggleAdvertising();
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  <img src={AdvertisingIcon} alt={"advertising"} />
                </ListItemIcon>
                <ListItemText
                  primary={intl.formatMessage({
                    id: I18nKey.DRAWER_LAYOUT_ADVERTISING,
                  })}
                  sx={{
                    opacity: open ? 1 : 0,
                    color: "#FFAB8C",
                    fontSize: 16,
                    "& .MuiTypography-root": {
                      fontFamily: "Lato",
                    },
                  }}
                />
                {open &&
                  (collapseAdvertising ? (
                    <ExpandMore sx={{ color: "#FFAB8C" }} />
                  ) : (
                    <ExpandLess sx={{ color: "#FFAB8C" }} />
                  ))}
              </ListItemButton>
              <Collapse
                in={!collapseAdvertising && open}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  <ListItemButton
                    sx={{
                      pl: 4,
                      background:
                        active === "promotionCalendar"
                          ? "linear-gradient(132.91deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 99%)"
                          : "none",
                    }}
                    onClick={() => {
                      setActive("promotionCalendar");
                      navigate(ROUTES.Ulysses);
                    }}
                    key={1}
                  >
                    <ListItemText
                      sx={{
                        color:
                          active === "promotionCalendar" ? "white" : "#FF6C37",
                        fontFamily: "Lato",
                        "& .MuiTypography-root": {
                          fontFamily: "Lato",
                        },
                        marginLeft: "30px",
                      }}
                      primary={intl.formatMessage({
                        id: I18nKey.DRAWER_LAYOUT_PROMOTIONAL_CALENDAR,
                      })}
                    />
                  </ListItemButton>
                </List>
              </Collapse>
            </ListItem>
          )}
        </List>
        <Divider />
      </Drawer>
      <Box
        component="main"
        className={classNames(classes.drawerHeaderBox, {
          mobile: isMobile,
          token: token,
          nav: nav,
        })}
      >
        <DrawerHeader />
        {token && user?.user?.email === "demo@ridgewoodpartner.com" ? (
          <LoadingScreen />
        ) : (
          children
        )}
      </Box>
      <Popover />
    </Box>
  );
};
export default Layout;
